import { useCallback, useMemo } from 'react';
import { useCallDashboard } from 'roger-call-dashboard';

import type { IConnectAgentState } from 'roger-call-dashboard/src/infrastructure/types';

interface IOptions {
  value: string;
  label: string;
  record: any;
  disabled?: boolean;
}

type ReturnValue = {
  loading: boolean;
  statuses: IOptions[];
  currentStatus: string | undefined;
  checkStatusByName: (name: string) => boolean;
  setStatus: (value: IConnectAgentState) => void;
};

export const useUserStatus = (): ReturnValue => {
  const { agent: a, isInitialized } = useCallDashboard();

  const agent = a || {};

  const loading = !agent || !isInitialized;

  const currentStatus = useMemo(() => {
    if (isInitialized && agent && agent.availabilityState)
      return agent.availabilityState.agentStateARN;

    return undefined;
  }, [agent, agent?.availabilityState?.agentStateARN, isInitialized]);

  const statuses = useMemo<IOptions[]>(() => {
    if (agent && agent?.states)
      return agent.states.map((el) => ({
        label: el.name,
        value: el.agentStateARN,
        record: el,
      }));

    return [];
  }, [agent, isInitialized]);

  const checkStatusByName = useCallback(
    (name: string) => {
      const status = statuses.find((el) => el.value === currentStatus);
      if (status) {
        return status.label.trim().toLowerCase() === name;
      }
      return false;
    },
    [statuses, currentStatus],
  );

  const setStatus = (value: IConnectAgentState) => {
    if (agent.setState) agent.setState(value);
  };

  return {
    loading,
    statuses,
    currentStatus,
    checkStatusByName,
    setStatus,
  };
};
